import { tmxApiCall, axiosApiCall } from './index'

// Utilities
import isEmpty from '../utility/isEmpty'
import LocalFile from '../utility/localFileUtil'
import Logger from '../utility/loggerUtil'
import Session from '../utility/sessionUtil'

export default {
  /**
   * Gets the packet 1 disclosure documents to show for a given store and loan model.
   * @param {String} storeId - The store identifier.
   * @param {String} productId - The loan model type identifier.
   * @param {String} suretyBond - The type of surety bond associated with the store and loan model.
   * @param {Boolean} migrateProduct - True if customer chose to migrate to a new product (or is required migrate upon refinance).
   * @param {String} disclosureType - The type of disclosure to query for.
   * @param {Boolean} isAddon - Is this transaction an add-on transaction?
   * @returns {Promise<[{label: String, url: String, description: String}]>} A collection of disclosure document details.
   */
  async getDisclosures (storeId, productId, suretyBond, migrateProduct, disclosureType, isAddon) {
    Logger.info('Started Get Account Disclosures query.')

    return tmxApiCall('get', '/documents', {
      params: {
        storeId,
        productId,
        suretyBond,
        migrateProduct,
        disclosureType,
        isAddon
      }
    })
  },

  /**
   * Gets the current signing status for the passed in loan.
   * @param {String} customerId - The customer identifier to check the status of.
   * @param {String} loanId - The loan identifier to check the status of.
   * @param {String} lmsId - The LMS identifier that the loan is associated with.
   * @param {String} signingType - The type of signing ceremony to initiate.
   * @param {Boolean} [iframe] - Tells us if the signing is occurring in an iframe or not.
   * @param {String} [redirectUrl] - The URL to redirect to after signing is complete (if redirect is configured on).
   * @returns {Promise<{signing: String, callback: String, status: {borrower: String, cosigner: String, envelope: String}}>} The signing URL for the current participant along with session status and callback URL.
   */
  async getSigningUrl (customerId, lmsId, loanId, signingType, iframe, redirectUrl) {
    Logger.info('Started Get Signing URL')

    return tmxApiCall('post', '/loanSigning', {
      customerId,
      lmsId,
      loanId,
      signingType,
      iframe,
      redirectUrl
    })
  },

  /**
   * Gets the current signing status for the passed in loan.
   * @param {String} loanId - The loan identifier to check the status of.
   * @param {String} lmsId - The LMS identifier that the loan is associated with.
   * @param {String} signingType - The type of signing ceremony to initiate.
   * @returns {Promise<{status: {borrower: String, cosigner: String, envelope: String}}>} The borrower and cosigner signing status
   */
  async getSigningStatus (loanId, lmsId, signingType) {
    Logger.info('Started Get Signing Status')

    return tmxApiCall('get', '/loanSigning', {
      params: {
        loanId,
        lmsId,
        signingType
      }
    })
  },

  async postDisclosureAcknowledgement (loanId, cognitoId, disclosureType, storeId, disclosures, loanBrand, borrowCustomerId, borrowInitials, borrowDate, borrowName, borrowUsername, borrowDob, borrowSsn, borrowAccountPhone, borrowHomeAddress, borrowMailAddress, borrowProfileId, coborrowCustomerId, coborrowInitials, coborrowDate, coborrowName, coborrowUsername, coborrowDob, coborrowSsn, coborrowAccountPhone, coborrowHomeAddress, coborrowMailAddress, coborrowProfileId) {
    Logger.info('Started post disclosure acknowledgement')

    const obj = {
      AccountNumber: loanId,
      storeId,
      disclosureType,
      loanBrand,
      disclosureAck: {
        cognitoId,
        disclosures,
        borrowerInfo: {
          customerId: borrowCustomerId,
          name: borrowName,
          username: borrowUsername,
          dob: borrowDob,
          ssn: borrowSsn,
          accountPhone: borrowAccountPhone,
          homeAddress: borrowHomeAddress,
          mailingAddress: borrowMailAddress,
          initials: borrowInitials,
          date: borrowDate,
          profileId: borrowProfileId
        },
        coborrowerInfo: {
          customerId: coborrowCustomerId,
          name: coborrowName,
          username: coborrowUsername,
          dob: coborrowDob,
          ssn: coborrowSsn,
          accountPhone: coborrowAccountPhone,
          homeAddress: coborrowHomeAddress,
          mailingAddress: coborrowMailAddress,
          initials: coborrowInitials,
          date: coborrowDate,
          profileId: coborrowProfileId
        }
      }
    }

    return tmxApiCall('post', '/disclosureacknowledgement', obj)
  },

  /**
   * Gets the document upload history.
   * @param {String} [taskId] - A identifier for the task related to the loan application.
   * @param {String} [loanId] - A identifier for the loan for the customer.
   * @param {String} lmsId - The LMS of the document history records we want.
   * @returns {Promise<{results: [{description: String}]}>} The details of previously uploaded documents.
   */
  async getLoanDocumentsUploadHistory (taskId, loanId, lmsId) {
    Logger.info('Started Get Document Upload History')

    return tmxApiCall('get', '/uploadLoanDocumentsHistory', {
      params: {
        taskId,
        loanId,
        lmsId
      }
    })
  },

  /**
   * Uploads loan documents to the designated LMS.
   * @param {String} [taskId] - A identifier for the task related to the loan application.
   * @param {String} [loanId] - A identifier for the loan for the customer.
   * @param {[{name: String, url: String, key: String, bucket: String}]} files - The information needed to take the files from the S3 bucket to NLS.
   * @param {String} lmsId - The LMS of the document being uploaded.
   * @param {String} [docType] - Optionally, the type of document being uploaded (required only for TLXe).
   */
  async uploadLoanDocuments (taskId, loanId, files, lmsId, docType) {
    Logger.info('Started Document Upload to NLS')

    return tmxApiCall('put', '/uploadLoanDocuments', {
      taskId,
      loanId,
      files,
      lmsId,
      docType
    })
  },

  /**
   * Gets documents associated with a loan and organized by categories.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with
   * @param {String} loanId - The LMS identifier for the loan
   * @returns {Promise<[{CategoryName: String, Description: String, Documents: [{DocumentName: String, Date: String, DocumentId: Number} | {Year: String, Documents: [{DocumentName: String, Date: String, DocumentId: Number}]}]}]>} A collection of document objects
   */
  async getLMSAccountDocs (lmsId, loanId) {
    Logger.info('Started Get LMS Account Docs')
    return tmxApiCall('get', '/lmsAccountDocuments', {
      params: {
        lmsId,
        loanId
      }
    })
  },

  /**
   * Get an object containing the url to view a specific lms account document
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with
   * @param {String} loanId - The LMS identifier for the loan
   * @param {String} documentId - The LMS document Id to request a url for viewing
   * @returns {Promise<[{DocumentName: String, Description: String, SignedUrl: String}]>} An object with a url to view a lms account document
   */
  async getLMSAccountDocUrl (lmsId, loanId, documentId) {
    Logger.info('Started Get LMS Account Docs')
    return tmxApiCall('get', '/lmsAccountDocuments', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        lmsId,
        loanId,
        documentId
      }
    })
  },

  /**
   * Adds a document history view record.
   * @param {String} loanId - The LMS identifier for the loan
   * @param {String} documentId - The LMS document Id to add a view history record.
   * @param {String} documentName = The document name to add to the view history record.
   */
  async addLMSAccountDocumentViewHistory (loanId, documentId, documentName) {
    Logger.info('Started Adding LMS Account Doc View History')
    return tmxApiCall('post', '/lmsAccountDocumentViewHistory', {
      loanId,
      documentId,
      documentName
    })
  },

  /**
   * Uploads a media file to a given signed url.
   * @param {String} uploadUrl - The signed upload URL to use.
   * @param {Blob|Buffer} file - The file contents to upload.
   * @param {Object} uploadFields - The S3 signed upload fields to include in the request.
   * @param {String} uploadFields.key - The S3 keys for the file (any folder path and filename).
   */
  async uploadMediaToSignedUrl (uploadUrl, file, uploadFields) {
    Logger.info('Started upload photo call.')

    const fileName = uploadFields.key.split('/').pop()
    const mimeType = LocalFile.getMimeType(uploadFields.key)

    const dataArr = Object.keys(uploadFields).map(key => {
      return {
        key,
        value: uploadFields[key]
      }
    })

    dataArr.unshift({ // ** Ordering of form data properties matters **
      key: 'Content-Type',
      value: mimeType
    })

    dataArr.push({ // ** Ordering of form data properties matters **
      key: 'file',
      value: file,
      fileName
    })

    const formData = getFormData(dataArr)

    return axiosApiCall({
      url: uploadUrl,
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  /**
   * Gets pre-signed AWS upload URLs for our loan documents.
   * @param {String} [taskId] - A identifier for the task related to the loan application.
   * @param {String} [loanId] - A identifier for the loan related to the documents.
   * @param {Promise<[{name: String, content: Base64, size: Number: ext: String, type: String}]>} files - A collection file objects with base64 strings representing each file.
   * @param {String} lmsId - The LMS of the document being uploaded.
   */
  async getLoanDocumentSignedUploadUrls (taskId, loanId, files, lmsId) {
    Logger.info('Started Get Loan Document Pre-Signed Upload URLs')

    return tmxApiCall('get', '/uploadLoanDocuments', {
      params: {
        files: JSON.stringify(files.map(file => ({ name: file.name, size: file.size, type: file.type }))),
        taskId,
        loanId,
        lmsId
      }
    })
  }
}

/**
 * Returns FormData from the passed in object collection.
 * @param {Array<{key: String, value: String|Number, [fileName]: String}>} objArr - The collection of key/value pair objects.
 * @returns {FormData} The form data to upload.
 */
const getFormData = (objArr) => {
  const data = new FormData()

  for (let i = 0; i < objArr.length; i++) {
    const key = objArr[i].key
    const value = objArr[i].value
    const fileName = objArr[i].fileName

    Logger.debug(`Form data ${key}: ${value}`)

    if (isEmpty(fileName)) {
      data.append(key, value)
    } else {
      data.append(key, value, fileName)
    }
  }

  return data
}
